// Etsy CSV -> core orders. Ported from etsy.gs.
// Etsy gives one row per line item; rows sharing an Order ID are one order.
// Quantity is kept as a line-item field (the legacy row-duplication is dropped).

import { SOURCE } from '../constants';
import { parseCsvText } from './parseCsv';
import { decodeHtmlEntities, cleanProduct, cleanPostcode, parseMoney } from '../lib/clean';
import { parseEtsyDate } from '../lib/dates';
import { etsyDeliveryService } from '../lib/delivery';
import { isEUCountry } from '../lib/countries';

function makeGetter(headerRow) {
  const idx = {};
  headerRow.forEach((h, i) => {
    idx[String(h).trim().toLowerCase()] = i;
  });
  const get = (row, name) => {
    const i = idx[name.toLowerCase()];
    return i == null ? '' : row[i] ?? '';
  };
  return { idx, get };
}

function rowToObj(headerRow, row) {
  const obj = {};
  headerRow.forEach((h, i) => {
    obj[h] = row[i] ?? '';
  });
  return obj;
}

// Variations -> { variation, buyerNotes } (legacy split on ",Personali").
function splitVariations(value) {
  if (!value) return { variation: '', buyerNotes: '' };
  const decoded = (s) =>
    decodeHtmlEntities(s.replace(/,/g, ' / ').replace(/:/g, ': ').trim());
  const marker = value.indexOf(',Personali');
  if (marker !== -1) {
    return {
      variation: decoded(value.slice(0, marker)),
      buyerNotes: decoded(value.slice(marker)),
    };
  }
  return { variation: decoded(value), buyerNotes: '' };
}

function buyerUserId(buyerCell, shipName) {
  if (!buyerCell) return shipName || '';
  const m = buyerCell.match(/\(([^)]+)\)/);
  return m ? m[1] : buyerCell;
}

export function parseEtsyOrders(text, rules) {
  const rows = parseCsvText(text);
  if (!rows.length) return [];
  const header = rows[0];
  const { get } = makeGetter(header);

  const groups = new Map(); // Order ID -> { header row, item rows }

  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    if (!row || row.every((c) => !c || String(c).trim() === '')) continue;
    const orderId = String(get(row, 'Order ID')).trim();
    if (!orderId) continue;
    if (!groups.has(orderId)) groups.set(orderId, []);
    groups.get(orderId).push(row);
  }

  const orders = [];
  for (const [orderId, itemRows] of groups) {
    const first = itemRows[0];
    const country = get(first, 'Ship Country');
    const shipName = decodeHtmlEntities(get(first, 'Ship Name'));

    const lineItems = itemRows.map((row, n) => {
      const itemName = get(row, 'Item Name');
      const { variation, buyerNotes } = splitVariations(get(row, 'Variations'));
      return {
        lineId: `li_${n + 1}`,
        product: decodeHtmlEntities(cleanProduct(itemName)),
        productRaw: itemName,
        variation,
        buyerNotes,
        quantity: Number(get(row, 'Quantity')) || 1,
        unitPrice: parseMoney(get(row, 'Price')),
        sku: get(row, 'SKU') || '',
        isLeather: itemName.toLowerCase().includes('leather'),
        sortOrder: n,
      };
    });

    const discount =
      parseMoney(get(first, 'Discount Amount')) + parseMoney(get(first, 'Shipping Discount'));

    orders.push({
      source: SOURCE.ETSY,
      platformOrderId: orderId,
      platformOrderId2: orderId,
      buyer: {
        userId: buyerUserId(get(first, 'Buyer'), shipName),
        name: shipName,
        email: '', // Etsy export carries no buyer email
      },
      shipping: {
        name: shipName,
        phone: '',
        address1: decodeHtmlEntities(get(first, 'Ship Address1')),
        address2: decodeHtmlEntities(get(first, 'Ship Address2')),
        townCity: decodeHtmlEntities(get(first, 'Ship City')),
        county: String(get(first, 'Ship State')).replace(/\bEngland\b/i, '').trim(),
        postcode: cleanPostcode(get(first, 'Ship Zipcode'), country),
        country,
        service: etsyDeliveryService(get(first, 'Order Shipping'), rules?.etsy),
        ioss: isEUCountry(country),
      },
      lineItems,
      money: {
        currency: get(first, 'Currency') || 'GBP',
        postage: parseMoney(get(first, 'Order Shipping')),
        discount,
        refund: 0,
      },
      dates: {
        ordered: parseEtsyDate(get(first, 'Sale Date')),
        paid: parseEtsyDate(get(first, 'Date Paid')) || null,
        postByPlatform: null, // Etsy has no platform post-by
      },
      raw: { etsy: itemRows.map((r) => rowToObj(header, r)) },
    });
  }

  return orders;
}
